<template>
  <v-dialog
    v-model="dialog"
    max-width="640"
    :persistent="saving"
  >
    <v-card v-if="current">
      <v-card-title class="pb-1">
        Gerar palavras-chave
        <v-spacer />
        <span class="text-caption text--secondary mr-2">
          {{ index + 1 }} de {{ queue.length }}
        </span>
        <v-btn icon small :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-progress-linear
        :value="progressPercent"
        color="primary"
        height="4"
      />
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-card outlined rounded="lg">
              <v-img
                :src="currentImage"
                :aspect-ratio="1"
                contain
                class="white"
              >
                <template v-slot:placeholder>
                  <div
                    class="d-flex fill-height align-center justify-center grey lighten-3"
                  >
                    <v-icon color="grey">mdi-image-off</v-icon>
                  </div>
                </template>
              </v-img>
            </v-card>
          </v-col>
          <v-col cols="12" sm="8">
            <v-chip
              v-if="current.type === 'COMBO'"
              color="primary"
              x-small
              class="mb-2"
            >
              Combo
            </v-chip>
            <h3 class="text-h6 mb-0">{{ current.name }}</h3>
            <p class="text--secondary mb-3">
              {{ current.description || "Sem descrição" }}
            </p>

            <v-combobox
              ref="keywordsField"
              v-model="keywords"
              label="Palavras-chave"
              placeholder="Ex.: cerveja, lata, gelada"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              hide-selected
              hide-no-data
              :disabled="saving"
              :loading="generating"
              hint="Enter salva e vai para o próximo. Digite e Enter para adicionar uma palavra."
              persistent-hint
            />
            <v-btn
              color="primary"
              text
              small
              class="mt-1 px-0"
              :loading="generating"
              :disabled="saving || generating"
              @click="regenerate"
            >
              <v-icon small left>mdi-auto-fix</v-icon>
              Gerar novamente
            </v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-btn text :disabled="saving" @click="skip">
          Ignorar
        </v-btn>
        <v-spacer />
        <v-btn
          color="success"
          :loading="saving"
          :disabled="generating"
          @click="save"
        >
          {{ isLast ? "Salvar e concluir" : "Salvar e próximo" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import merchProductsService from "@/services/admin/merchProducts";

export default {
  name: "GenerateKeywordsDialog",
  data() {
    return {
      dialog: false,
      queue: [],
      index: 0,
      keywords: [],
      generating: false,
      saving: false,
      error: null,
      requestToken: 0,
      prefetchCache: {},
    };
  },
  computed: {
    current() {
      return this.queue[this.index] || null;
    },
    isLast() {
      return this.index >= this.queue.length - 1;
    },
    progressPercent() {
      if (!this.queue.length) return 0;
      return ((this.index + 1) / this.queue.length) * 100;
    },
    currentImage() {
      const image = (this.current?.images || [])[0];
      return image?.url || image || null;
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        window.addEventListener("keydown", this.onGlobalKeydown, true);
      } else {
        window.removeEventListener("keydown", this.onGlobalKeydown, true);
        this.reset();
      }
    },
  },
  methods: {
    open(products = []) {
      this.queue = (products || []).map((product) =>
        structuredClone(product)
      );
      this.index = 0;
      this.keywords = [];
      this.error = null;
      this.saving = false;
      this.generating = false;
      this.prefetchCache = {};
      this.requestToken += 1;
      if (!this.queue.length) return;
      this.dialog = true;
      this.loadCurrent();
    },
    close() {
      this.dialog = false;
    },
    reset() {
      this.requestToken += 1;
      this.queue = [];
      this.index = 0;
      this.keywords = [];
      this.error = null;
      this.saving = false;
      this.generating = false;
      this.prefetchCache = {};
    },
    normalizeKeywords(keywords) {
      if (!Array.isArray(keywords)) return [];
      const seen = new Set();
      const normalized = [];
      for (const keyword of keywords) {
        const value =
          typeof keyword === "string"
            ? keyword.trim()
            : String(keyword || "").trim();
        if (!value) continue;
        const key = value.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        normalized.push(value);
      }
      return normalized;
    },
    productImages(product) {
      return (product.images || []).filter(
        (image) => typeof image === "string" || typeof image?.url === "string"
      ).map((image) => (typeof image === "string" ? image : image.url));
    },
    async loadCurrent() {
      const product = this.current;
      if (!product) {
        this.close();
        return;
      }
      const token = ++this.requestToken;
      this.keywords = [];
      this.error = null;
      this.generating = true;
      try {
        const keywords = await this.generateForProduct(product);
        if (token !== this.requestToken) return;
        this.keywords = keywords;
        this.focusKeywords();
        this.prefetchNext();
      } catch (error) {
        if (token !== this.requestToken) return;
        this.error = error.message || "Erro ao gerar palavras-chave";
      } finally {
        if (token === this.requestToken) this.generating = false;
      }
    },
    async generateForProduct(product) {
      if (this.prefetchCache[product.id]) {
        return this.prefetchCache[product.id];
      }
      const promise = merchProductsService
        .generateMerchProductKeywords({
          name: product.name,
          description: product.description,
          type: product.type,
          keywords: [],
        })
        .then((response) => this.normalizeKeywords(response.keywords))
        .catch((error) => {
          delete this.prefetchCache[product.id];
          throw error;
        });
      this.prefetchCache[product.id] = promise;
      return promise;
    },
    prefetchNext() {
      const next = this.queue[this.index + 1];
      if (!next || this.prefetchCache[next.id]) return;
      this.generateForProduct(next).catch(() => {});
    },
    async regenerate() {
      const product = this.current;
      if (!product) return;
      delete this.prefetchCache[product.id];
      const token = ++this.requestToken;
      this.error = null;
      this.generating = true;
      try {
        const keywords = await this.generateForProduct(product);
        if (token !== this.requestToken) return;
        this.keywords = keywords;
        this.focusKeywords();
      } catch (error) {
        if (token !== this.requestToken) return;
        this.error = error.message || "Erro ao gerar palavras-chave";
      } finally {
        if (token === this.requestToken) this.generating = false;
      }
    },
    focusKeywords() {
      this.$nextTick(() => {
        const input = this.$refs.keywordsField?.$el?.querySelector("input");
        input?.focus();
      });
    },
    onGlobalKeydown(event) {
      if (event.key !== "Enter" || event.repeat) return;
      if (!this.dialog || this.saving || this.generating) return;
      const target = event.target;
      if (target?.closest?.("button, .v-btn")) return;
      const isField =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA");
      if (isField && String(target.value || "").trim()) return;
      event.preventDefault();
      event.stopPropagation();
      this.save();
    },
    async save() {
      const product = this.current;
      if (!product || this.saving || this.generating) return;
      const keywords = this.normalizeKeywords(this.keywords);
      if (!keywords.length) {
        this.error = "Adicione ao menos uma palavra-chave ou ignore o produto";
        return;
      }
      this.saving = true;
      this.error = null;
      try {
        const response = await merchProductsService.updateMerchProduct(
          product.id,
          {
            name: product.name,
            description: product.description,
            type: product.type || "PRODUCT",
            images: this.productImages(product),
            keywords,
          }
        );
        this.$emit("updated", response.merchProduct);
        this.goNext();
      } catch (error) {
        this.error = error.message || "Erro ao salvar as palavras-chave";
      } finally {
        this.saving = false;
      }
    },
    skip() {
      if (this.saving) return;
      this.goNext();
    },
    goNext() {
      if (this.isLast) {
        this.close();
        return;
      }
      this.index += 1;
      this.loadCurrent();
    },
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onGlobalKeydown, true);
  },
};
</script>
